/*
 * Email validation for the theme's storefront forms
 *
 * Covers every form marked [data-email-form] - the newsletter, contact,
 * comment and recipient captures among them - including markup the theme
 * editor swaps in, which is why every listener is delegated. The form names
 * its email field [data-email-field] and points [data-email-error-id] at an
 * always-present hidden message element.
 *
 * The one rule added on top of the browser's email parser is DOTTED_DOMAIN
 * below. It is enforced through setCustomValidity() rather than by cancelling
 * the submit event: Shopify's storefront form protection watches submit events
 * too and drives its own captcha navigation from them regardless of
 * preventDefault, so a submit event for a bad address must never be dispatched
 * at all. With the custom validity set, the browser refuses the submission
 * itself and fires `invalid` instead - and `invalid` is what this script
 * restyles into the theme's inline message.
 *
 *   invalid  the browser rejected the field - its own parser, `required`, or
 *            our custom validity - and is about to show its bubble; take that
 *            over and say the same thing in the page. invalid does not
 *            bubble, so this listens in the capture phase.
 *   submit   last resort for a value the sync below never saw (a restored
 *            session can repopulate a field without firing events). This path
 *            can lose to the captcha script, which is why it is the fallback
 *            and not the mechanism.
 *
 * The message text is rendered by Liquid so it is translated; this only
 * decides when to show it. A form with no message element still refuses a bad
 * address - the browser's bubble then carries the same translated text.
 */
(function () {
  // The HTML spec's idea of a valid email address deliberately allows a bare
  // host - name@localhost is a real address on an intranet - so the browser
  // accepts "name@example" without complaint. An address a shop can write to
  // has to be reachable from the public internet, so it needs a dotted
  // suffix. That is the one rule we add on top of the browser's parser, which
  // is otherwise stricter and better maintained than anything worth writing
  // here.
  var DOTTED_DOMAIN = /\.[^\s@.]{2,}$/;

  function fieldOf(form) {
    return form.querySelector('[data-email-field]');
  }

  function errorOf(form) {
    var id = form.getAttribute('data-email-error-id');
    return id ? document.getElementById(id) : null;
  }

  // Add or remove our message id without disturbing any other id already
  // there - a server-side error is announced through the same attribute.
  function describe(input, id, on) {
    var ids = (input.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
    var at = ids.indexOf(id);
    if (on && at === -1) ids.push(id);
    if (!on && at !== -1) ids.splice(at, 1);
    if (ids.length) input.setAttribute('aria-describedby', ids.join(' '));
    else input.removeAttribute('aria-describedby');
  }

  function clearError(form) {
    var input = fieldOf(form);
    var error = errorOf(form);
    if (!input || !error) return;
    error.hidden = true;
    error.textContent = '';
    input.removeAttribute('aria-invalid');
    input.classList.remove('is-invalid');
    describe(input, error.id, false);
  }

  function showError(form, message, focus) {
    var input = fieldOf(form);
    var error = errorOf(form);
    if (!input || !error) return;
    // Unhide before writing the text: an alert that is still hidden when its
    // content changes may not be announced. aria-describedby then carries the
    // message to anyone who reaches the field afterwards, and focus puts them
    // there immediately.
    error.hidden = false;
    error.textContent = message;
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('is-invalid');
    describe(input, error.id, true);
    if (focus) input.focus();
  }

  // True when the browser's parser accepts the value but the dotted-suffix
  // rule does not - the gap setCustomValidity() has to plug.
  function bareDomain(input) {
    var value = input.value.trim();
    if (!value || input.validity.typeMismatch) return false;
    return !DOTTED_DOMAIN.test(value.slice(value.lastIndexOf('@') + 1));
  }

  function syncValidity(form, input) {
    // The message must be non-empty even when the attribute is missing - an
    // empty string would clear the custom validity and let the bad address
    // through. The attribute carries the translated text; the literal is the
    // last ditch.
    input.setCustomValidity(
      bareDomain(input) ? form.getAttribute('data-error-invalid') || 'Invalid email address' : ''
    );
  }

  // Reads validity as state rather than calling checkValidity(), which would
  // fire another invalid event and re-enter the handler below.
  function messageFor(form, input) {
    var value = input.value.trim();
    // An optional email field (the gift-card recipient) is allowed to stay
    // empty - only a required one gets the required message.
    if (!value) {
      return input.required ? form.getAttribute('data-error-required') || '' : '';
    }
    if (input.validity.typeMismatch || bareDomain(input)) {
      return form.getAttribute('data-error-invalid') || '';
    }
    return '';
  }

  document.addEventListener(
    'invalid',
    function (event) {
      var input = event.target;
      if (!input.matches || !input.matches('[data-email-field]')) return;

      var form = input.closest('[data-email-form]');
      if (!form || !errorOf(form)) return;

      var message = messageFor(form, input);
      // Nothing of our own to say, so leave the browser to report it.
      if (!message) return;

      // Cancelling only suppresses the bubble - the submission stays blocked.
      event.preventDefault();
      // On a form with several fields the browser may be about to put its own
      // bubble and focus on an earlier invalid one - only pull focus when
      // this field is the first thing wrong.
      var firstInvalid = form.querySelector(':invalid:not(fieldset)');
      showError(form, message, !firstInvalid || firstInvalid === input);
    },
    true
  );

  // Last resort - see the header. If a bad value reaches an actual submit
  // event the sync below never saw it, so block and report here.
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-email-form]');
    if (!form) return;

    var input = fieldOf(form);
    var error = errorOf(form);
    // Nothing to report through, so do not stand in the way of the submit.
    if (!input || !error) return;

    // A field the browser would not validate - disabled along with the
    // collapsed gift-recipient fieldset, say - must not block the submit,
    // whatever stale value it holds.
    if (!input.willValidate) {
      clearError(form);
      return;
    }

    var message = messageFor(form, input);
    if (message) {
      event.preventDefault();
      showError(form, message, true);
      return;
    }

    clearError(form);
  });

  // Keep the custom validity current as the value changes, so a submission
  // attempt is refused by the browser itself, and clear our message as soon
  // as the shopper starts correcting the field.
  document.addEventListener('input', function (event) {
    var input = event.target.closest ? event.target.closest('[data-email-field]') : null;
    if (!input) return;
    var form = input.closest('[data-email-form]');
    if (!form) return;
    syncValidity(form, input);
    clearError(form);
  });

  // Autofill settles on change without a keystroke ever firing input.
  document.addEventListener('change', function (event) {
    var input = event.target.closest ? event.target.closest('[data-email-field]') : null;
    if (!input) return;
    var form = input.closest('[data-email-form]');
    if (form) syncValidity(form, input);
  });

  // A restored value can arrive with no events at all, so re-sync in the
  // capture phase of the gestures that start a submission - before the
  // browser runs its pre-submit validation.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest ? event.target.closest('button, input[type="submit"]') : null;
      if (!button || button.type !== 'submit') return;
      // .form also resolves a button associated through the form="" attribute,
      // which can sit outside the form element (the sticky add-to-cart bar).
      var form = button.form;
      if (!form || !form.matches('[data-email-form]')) return;
      var input = fieldOf(form);
      if (input) syncValidity(form, input);
    },
    true
  );

  document.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Enter') return;
      var input = event.target.closest ? event.target.closest('[data-email-field]') : null;
      if (!input) return;
      var form = input.closest('[data-email-form]');
      if (form) syncValidity(form, input);
    },
    true
  );
})();
